"use client";

// AccessControl — how far into you ONE app may read: the depth as a segmented
// track (standard · personal · private) and, beyond standard, whether it asks
// first, as a switch. The settings family's two controls and no more
// (chat-settings.css).
//
// It is never a list. It sits where the app already is: in a connection's own
// window (its settings section) and beside an api key in the api view. Every
// app starts at standard: trusted, work and public facts, the behaviour from
// before the ring.

import { DEPTHS, type Depth } from "../../lib/self/ring";
import { DEPTH_WORDS, type RingApp } from "../../lib/self/ringView";
import { CONNECT_TOOLS } from "../../lib/connect/tools";
import { relTime } from "../../lib/os/tree";
import "../dashboard/chat-settings.css";
import "../you/ring.css";

/** what the app reads, in words, with its ask-first said when it matters */
export function readsWords(app: Pick<RingApp, "depth" | "askFirst">): string {
  if (app.depth === "standard") return DEPTH_WORDS.standard.says;
  return app.askFirst ? `${DEPTH_WORDS[app.depth].says}, when you allow it` : DEPTH_WORDS[app.depth].says;
}

/** the access sheet's name for readsWords */
export const says = readsWords;

/** what an app is and when it last read, in one quiet line */
export function what(app: RingApp): string {
  const seen = app.lastSeenAt ? `last read ${relTime(app.lastSeenAt)}${relTime(app.lastSeenAt) === "just now" ? "" : " ago"}` : "not used yet";
  if (app.kind === "api_key") return `api key${app.keyPrefix ? ` ${app.keyPrefix}…` : ""} · ${seen}`;
  const tool = CONNECT_TOOLS.find((t) => t.id === app.toolId)?.name ?? null;
  return `${tool ? `${tool} · ` : ""}${seen}`;
}

/** the same, as one whole sentence: "it reads your trusted, work and public facts, and …" */
export function readsSentence(app: Pick<RingApp, "depth" | "askFirst">): string {
  const base = "it reads your trusted, work and public facts";
  if (app.depth === "standard") return `${base}.`;
  const more = app.depth === "private" ? "the ones you keep to yourself and your private ones" : "the ones you keep to yourself";
  return `${base}, and ${more}${app.askFirst ? ", each time you allow it" : ""}.`;
}

export default function AccessControl({
  app,
  labelledBy,
  onSet,
}: {
  app: RingApp;
  /** the id of whatever names this app on the page (the track is labelled by it) */
  labelledBy: string;
  onSet: (app: RingApp, depth: Depth, askFirst: boolean) => void;
}) {
  const at = Math.max(0, DEPTHS.indexOf(app.depth));
  const id = `access-${app.kind}-${app.id}`;
  return (
    <div className="prv-ctl">
      <div
        className="cs-seg"
        role="radiogroup"
        aria-labelledby={labelledBy}
        style={{ "--n": DEPTHS.length, "--at": at } as React.CSSProperties}
      >
        <span className="cs-seg-lift" aria-hidden="true" />
        {DEPTHS.map((d: Depth) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={d === app.depth}
            data-noswipe
            onClick={() => d !== app.depth && onSet(app, d, app.askFirst)}
          >
            {DEPTH_WORDS[d].label}
          </button>
        ))}
      </div>
      <span className="prv-ask-first" data-off={app.depth === "standard" || undefined} aria-hidden={app.depth === "standard" || undefined}>
        <label htmlFor={`${id}-ask`}>ask first</label>
        <button
          id={`${id}-ask`}
          type="button"
          role="switch"
          className="cs-switch"
          aria-checked={app.askFirst}
          disabled={app.depth === "standard"}
          data-noswipe
          onClick={() => onSet(app, app.depth, !app.askFirst)}
        >
          <span className="cs-thumb" />
        </button>
      </span>
    </div>
  );
}
